<script module>
  export const ADULT = {
    min: 28,
    max: 59
  }

  export const YOUTH = {
    max: 27
  }

  export const SENIOR = {
    min: 60
  }

  export const i18n = {
    en: {
      ADULT: 'Adults',
      SENIOR: 'Seniors',
      YOUTH: 'Youths and children',
      '%s-%s years': {
        'one:one': '%s-%s year',
        'one:other': '%s-%s years',
        'other:other': '%s-%s years',
        'other:one': '%s-%s year'
      },
      '%s+ years': {
        one: '%s+ year',
        other: '%s+ years'
      }
    },
    sv: {
      ADULT: 'Vuxna',
      SENIOR: 'Seniorer',
      YOUTH: 'Ungdommar och barn',
      '%s-%s years': {
        'one:one': '%s-%s år',
        'one:other': '%s-%s år',
        'other:other': '%s-%s år',
        'other:one': '%s-%s år'
      },
      '%s+ years': {
        one: '%s+ år',
        other: '%s+ år'
      },
      'Age (on departure date)': 'Ålder (vid avresedatum)',
      'The optimal pass will be calulated depending on age when traveling':
        'Den optimala passet kommer att beräknas beroende på ålder vid resan'
    },
    da: {
      ADULT: 'Voksne',
      SENIOR: 'Seniorer',
      YOUTH: 'Ungdommar og børn',
      '%s-%s years': {
        'one:one': '%s-%s år',
        'one:other': '%s-%s år',
        'other:other': '%s-%s år',
        'other:one': '%s-%s år'
      },
      '%s+ years': {
        one: '%s+ år',
        other: '%s+ år'
      },
      'Age (on departure date)': 'Alder (på afrejsedato)',
      'The optimal pass will be calulated depending on age when traveling':
        'Den optimale biljett vil blive beregnet ud fra alderen på rejsedagen'
    }
  }
</script>

<script>
  import { register } from '$lib/utils/text.svelte.js'
  import DateInput from '$lib/DateInput.svelte'
  import Counter from '$lib/Counter.svelte'

  const AGES = { ADULT, YOUTH, SENIOR, UNKNOWN: {} }

  const [today] = new Date().toISOString().split('T')

  /**
   * @type {{
   *   size?: 'sm'|'lg',
   *   min?: number,
   *   max?: number,
   *   minBirthDate?: string,
   *   intermediate?: boolean,
   *   inputMode?: 'age'|'birthDate',
   *   passengers?: Schema.PassengerPlaceholderInput[],
   *   label?: Svelte.Snippet<[Schema.PassengerType|'UNKNOWN']>,
   *   onchange?: function(Schema.PassengerPlaceholderInput[]): void
   * }}
   */
  let {
    max = 5,
    size = 'sm',
    inputMode = 'age',
    minBirthDate = today,
    intermediate = $bindable(false),
    min = inputMode === 'age' ? 0 : 1,
    passengers = $bindable([
      { type: inputMode === 'age' ? 'ADULT' : undefined }
    ]),
    label,
    onchange
  } = $props()

  let text = register(i18n)

  let adults = $derived(
    passengers.filter((passenger) => passenger.type === 'ADULT')
  )
  let youths = $derived(
    passengers.filter((passenger) => passenger.type === 'YOUTH')
  )
  let seniors = $derived(
    passengers.filter((passenger) => passenger.type === 'SENIOR')
  )

  // This is needed to preserve context hierarchy for compiled WebComponents
  // TODO: See if it is still needed after migrating embeds to Runes
  let labels = $derived({
    ADULT: text('ADULT'),
    YOUTH: text('YOUTH'),
    SENIOR: text('SENIOR')
  })

  const oncounterchange = (type, prev) => (value) => {
    if (type === 'UNKNOWN') {
      type = undefined
    }

    if (value > prev) {
      passengers = [...passengers, { type }]
    } else {
      const index = passengers.findLastIndex(
        (passenger) => passenger.type === type
      )
      passengers = [
        ...passengers.slice(0, index),
        ...passengers.slice(index + 1)
      ]
    }

    onchange?.(passengers)
  }

  const withAge = (passenger, value) => {
    const age = +value
    const { min, max } = AGES[passenger.type]
    if ((!min || age >= min) && (!max || age <= max)) {
      return { age, birthDate: undefined }
    }
  }

  const update = (passenger, attrs) => {
    const index = passengers.indexOf(passenger)
    passengers = [
      ...passengers.slice(0, index),
      { ...passenger, ...attrs },
      ...passengers.slice(index + 1)
    ]
    onchange?.(passengers)
  }

  const handlechange = (passenger, value) => {
    const props = withAge(passenger, value)
    if (props) update(passenger, props)
    intermediate = false
  }

  const onageblur = (passenger) => (event) => {
    clearTimeout(timeout)
    handlechange(passenger, event.target.value)
  }

  let timeout
  const onageinput = (passenger) => (event) => {
    intermediate = true
    clearTimeout(timeout)
    timeout = setTimeout(
      (value) => handlechange(passenger, value),
      1750,
      event.target.value
    )
  }

  const onagekeydown = (passenger) => (event) => {
    if (event.key === 'Enter') {
      clearTimeout(timeout)
      handlechange(passenger, event.target.value)
    }
  }
</script>

{#snippet group({ subset, type })}
  {@const limit = AGES[type]}
  <Counter
    {size}
    {min}
    name={type}
    formnovalidate
    value={subset.length}
    max={max - (passengers.length - subset.length)}
    onchange={oncounterchange(type, subset.length)}>
    {#if label}
      {@render label(type)}
    {:else if labels[type]}
      <span class="label">
        {labels[type]}
        <span class="description">
          {'min' in limit && 'max' in limit
            ? text`${limit.min}-${limit.max} years`
            : 'min' in limit
              ? text`${limit.min}+ years`
              : 'max' in limit
                ? text`0-${limit.max} years`
                : null}
        </span>
      </span>
    {/if}
  </Counter>
  {#if type !== 'ADULT'}
    {#each subset as passenger, index}
      {@const id = `passenger-${passenger.type}-${index}`}
      {#if inputMode === 'birthDate'}
        <DateInput
          {id}
          required
          name="UNKNOWN-birthDate"
          placeholder={text`Date of birth`}
          value={passenger.birthDate}
          max={minBirthDate}
          action={(node) => {
            if (index === subset.length - 1) {
              node.focus()
            }
          }}
          onchange={(birthDate) =>
            update(passenger, {
              birthDate,
              age: undefined,
              type: undefined
            })} />
      {:else}
        <label class="field" for={id}>
          <span class="label">
            {text`Age (on departure date)`}
          </span>
          <input
            {id}
            required
            class="input"
            name="{type}-age"
            value={passenger.age}
            min={limit.min}
            max={limit.max}
            type="number"
            pattern="\\d+"
            onkeydown={onagekeydown(passenger)}
            oninput={onageinput(passenger)}
            onblur={onageblur(passenger)} />
        </label>
      {/if}
    {/each}
  {/if}
{/snippet}

<div class="passengers" class:sm={size === 'sm'}>
  <div class="main">
    <div class="counters">
      {#if inputMode === 'age'}
        {@render group({ subset: adults, type: 'ADULT' })}
        <hr class="divider" />
        {@render group({ subset: youths, type: 'YOUTH' })}
        <hr class="divider" />
        {@render group({ subset: seniors, type: 'SENIOR' })}
      {:else}
        {@render group({ subset: passengers, type: 'UNKNOWN' })}
      {/if}
    </div>
  </div>
</div>

<style>
  .passengers {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    padding: 0;
    position: relative;
    white-space: normal;
  }

  .main {
    display: flex;
    justify-content: center;
    flex-direction: column;
    flex: 1 1 auto;
  }

  .sm {
    font-size: 1rem;
  }

  .label {
    font-weight: bold;
  }

  .description {
    display: block;
    font-weight: normal;
    color: rgb(var(--rgb-muted));
  }

  .field .label {
    font-size: 0.9375rem;
    font-weight: normal;
  }

  .counters {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin-top: 0.5rem;
  }

  .divider {
    height: 0;
    margin-top: 0.3rem;
    border-bottom: 0.5px solid rgba(var(--rgb-muted), 0.2);
  }

  .field {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.8rem 1.25rem;
    margin-top: -0.2rem;
    position: relative;
    background: var(--background-alt);
    border-radius: var(--border-radius-xs);
  }

  .sm .field {
    padding: 0.5rem;
  }

  .field + .field {
    margin-top: -0.3rem;
  }

  .input {
    display: block;
    width: 3rem;
    padding: 0.25rem 0.5rem 0.3rem 0.5rem;
    margin-left: auto;
    font-size: 1rem;
    background: rgb(var(--rgb-white));
    border-radius: 0.3rem;
    border: 0;
  }
</style>
